import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert
} from '@mui/material';
import TemplateRenderer from './TemplateRenderer';

/**
 * StatementReviewDisplay - Reusable component for displaying witness statement content
 * Used in both the declaration/review step and the read-only view
 */
const StatementReviewDisplay = ({ statement, formData }) => {
  // Use formData if provided (during creation), otherwise use statement.draft_data (read-only view)
  const data = formData || statement?.draft_data || statement;

  console.log('[StatementReviewDisplay] Rendering with data:', data);
  console.log('[StatementReviewDisplay] Has source_event:', !!data?.source_event);
  console.log('[StatementReviewDisplay] Has narrative:', !!data?.narrative);
  console.log('[StatementReviewDisplay] Has witness:', !!data?.witness);
  console.log('[StatementReviewDisplay] Witness data:', data?.witness);
  console.log('[StatementReviewDisplay] Data keys:', data ? Object.keys(data) : 'no data');

  return (
    <Box>
      {/* Step 0: System Context - Full Display */}
      {data?.source_event && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            System Recorded Context Information
          </Typography>

          {/* Capture Image with Bounding Box */}
          {data.source_event.captureS3Key && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>Captured Image</Typography>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={data.source_event.captureMetadata?.capturedImageUrl ||
                       `https://${data.source_event.captureS3Key}`}
                  alt="Captured scene"
                  style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
                />
                {data.source_event.captureMetadata?.boundingBox && (
                  <Box
                    sx={{
                      position: 'absolute',
                      border: '3px solid #00ff00',
                      boxShadow: '0 0 10px rgba(0,255,0,0.5)',
                      left: `${data.source_event.captureMetadata.boundingBox.Left * 100}%`,
                      top: `${data.source_event.captureMetadata.boundingBox.Top * 100}%`,
                      width: `${data.source_event.captureMetadata.boundingBox.Width * 100}%`,
                      height: `${data.source_event.captureMetadata.boundingBox.Height * 100}%`,
                    }}
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Person Information */}
          {data.primary_person && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>Identified Person</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {data.primary_person.image && (
                  <Box>
                    <img
                      src={data.source_event.captureMetadata?.personImage}
                      alt="Person reference"
                      style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 4 }}
                    />
                  </Box>
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {data.primary_person.first_name} {data.primary_person.last_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID: {data.primary_person.person_id}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {data.primary_person.physical_attributes?.gender},
                    {data.primary_person.physical_attributes?.age_group}
                  </Typography>
                  {data.source_event.captureMetadata?.matchAccuracy && (
                    <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                      Match: {(data.source_event.captureMetadata.matchAccuracy * 100).toFixed(1)}%
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {/* Event Details */}
          <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Event Details</Typography>
            <Typography variant="body2">
              <strong>Date/Time:</strong> {new Date(data.source_event.event_timestamp).toLocaleString()}
            </Typography>
            {data.source_event.captureMetadata?.address && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Location:</strong> {data.source_event.captureMetadata.address.formatted}
              </Typography>
            )}
          </Box>
        </Paper>
      )}

      {/* Step 1: What Happened - Full Display */}
      {data?.narrative && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            What Happened
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">When did this happen?</Typography>
            <Typography variant="body1">{data.narrative.stated_time || 'Not provided'}</Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">Where were you positioned?</Typography>
            <Typography variant="body1">{data.narrative.stated_position || 'Not provided'}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">What happened? (Your full account)</Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
              {data.narrative.account || 'Not provided'}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Step 2: Identification - Full Display */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Identification
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Details about any persons you identified. (This step can be completed later if needed)
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          The ADVOKATE framework for identification assertions will be completed in the next version.
          You can skip this step for now and submit your statement.
        </Alert>
      </Paper>

      {/* Template Fields - Show resolved tags */}
      {statement?.draft_data?.template_snapshot?.config?.sections && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Template Fields
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Additional information from the template:
          </Typography>
          <TemplateRenderer
            statement={statement}
            formData={formData}
            onFieldChange={null}
          />
        </Paper>
      )}

      {/* Declaration - Full Display */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom>
          Declaration
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {data?.declaration?.text || 'I declare that this statement is true to the best of my knowledge and belief.'}
        </Typography>
        {data?.witness && (
          <>
            <Typography variant="body2" sx={{ mt: 2 }}>
              <strong>Account ID:</strong> {data.witness.account_id}
            </Typography>
            <Typography variant="body2">
              <strong>Handle:</strong> {data.witness.handle}
            </Typography>
            <Typography variant="body2">
              <strong>Email:</strong> {data.witness.email}
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default React.memo(StatementReviewDisplay);
